import { useState } from "react";

type UploadState = {
  uploading: boolean;
  progress: number;
};

type FileUploadState = {
  [id: string]: {
    [fileId: string]: UploadState;
  };
};

const defaultUploadState: UploadState = { uploading: false, progress: 0 };

export function useFileUpload() {
  const [fileUploadStates, setFileUploadStates] = useState<FileUploadState>({});

  const onProgress = (id: string, fileId: string, newProgress: number) => {
    setFileUploadStates((prevStates) => ({
      ...prevStates,
      [id]: {
        ...prevStates[id],
        [fileId]: {
          ...(prevStates[id]?.[fileId] || defaultUploadState),
          progress: newProgress,
        },
      },
    }));
  };

  const onUploadStatusChange = (
    id: string,
    fileId: string,
    uploading: boolean,
  ) => {
    setFileUploadStates((prevStates) => ({
      ...prevStates,
      [id]: {
        ...prevStates[id],
        [fileId]: {
          ...(prevStates[id]?.[fileId] || defaultUploadState),
          uploading,
        },
      },
    }));
  };

  const getUploadState = (id: string, fileId: string): UploadState =>
    fileUploadStates[id]?.[fileId] || defaultUploadState;

  // Keyed by a stable per-file id, so removing one file
  // can never affect another file's tracked progress/uploading state.
  const remove = (id: string, fileId: string) => {
    setFileUploadStates((prevStates) => {
      if (!prevStates[id]?.[fileId]) {
        return prevStates;
      }

      const updated = { ...prevStates[id] };
      delete updated[fileId];

      return { ...prevStates, [id]: updated };
    });
  };

  return {
    onProgress,
    onUploadStatusChange,
    getUploadState,
    remove,
  };
}
